import { MutableRefObject } from "react";

import { parallelArray } from "@impero-com/common/utils/form/formAtom/validation";
import { toSplitAtomValidationError } from "@impero-com/common/utils/form/splitFormAtom";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { uuidRegEx } from "@impero-com/common/utils/uuid";
import { isEmpty } from "lodash-es";
import memoizeOne from "memoize-one";
import * as Yup from "yup";

import {
    CurrencyCode,
    InitialFieldType,
    OrganizationCurrency,
    SubsequentFieldType,
    DataSheetAssignmentsListDto,
} from "api/generated/model";
import {
    CURRENCY_CODE_PATTERN,
    DATAGRID_MAX_NUMBER_OF_COLUMNS,
    DATAGRID_MAX_NUMBER_OF_ROWS,
} from "components/fieldBuilder/consts";
import {
    DEFAULT_DRAWER_BEHAVIOR,
    DrawerBehavior,
} from "components/fieldBuilder/fieldDrawer";
import { Field } from "components/fieldBuilder/models";
import {
    FieldTypeInfo,
    findDependency,
    getCommonFieldSchemaProperties,
    getCommonFieldTypeConditionalValues,
    getCommonFieldTypeDefaultValue,
    getCommonFieldTypeDrawerBehavior,
    getCommonFieldTypeInfo,
    getCommonFieldTypeSchema,
} from "components/fieldBuilder/utils";
import dataSheetSvg from "img/field/datasheet.svg";
import moneySvg from "img/field/money.svg";
import tableSvg from "img/field/table.svg";
import { generate } from "utils/guid";

import { FieldTypeName } from "./types";

type DataSheetFieldType = Extract<InitialFieldType, { type: "DataSheet" }>;

export type ControlFieldType = InitialFieldType | SubsequentFieldType;

export type MoneyInputFieldType = Extract<
    ControlFieldType,
    { type: "MoneyInput" }
>;

export type ControlField =
    | Field<InitialFieldType, string>
    | Field<SubsequentFieldType, string>;

export type ControlFieldTypeName =
    | FieldTypeName<InitialFieldType>
    | FieldTypeName<SubsequentFieldType>;

export const getFieldTypeInfo = memoizeOne(() => {
    const fieldTypeInfo: Record<ControlFieldTypeName, FieldTypeInfo> = {
        DataSheet: {
            label: t("CreateControl_TaskType_DataSheet"),
            svg: dataSheetSvg,
        },
        DataGrid: {
            label: t("CreateControl_TaskType_DataGrid"),
            svg: tableSvg,
        },
        MoneyInput: {
            label: t("CreateControl_TaskType_Money"),
            svg: moneySvg,
        },
        ...getCommonFieldTypeInfo(),
    };

    return (fieldTypeName: ControlFieldTypeName): FieldTypeInfo => {
        const info = fieldTypeInfo[fieldTypeName];
        if (!info) {
            throw new Error("Unknown type of field " + fieldTypeName);
        }
        return info;
    };
});

export function getNewTaskDefaultCurrency(
    currencies: OrganizationCurrency[]
): CurrencyCode | undefined {
    return (currencies.find(({ isDefault }) => isDefault) ?? currencies.at(0))
        ?.code;
}

/**
 * The organization can disable a currency that a task already uses. The task keeps it, so it stays
 * selectable.
 */
export function getDefaultCurrencyOptions(
    currencies: OrganizationCurrency[],
    selectedCurrency: CurrencyCode,
    language: string
): Array<{ label: string; value: CurrencyCode }> {
    const codes = currencies.map(({ code }) => code);
    const names = new Intl.DisplayNames([language], { type: "currency" });
    return (
        codes.includes(selectedCurrency) ? codes : [...codes, selectedCurrency]
    )
        .map((code) => ({
            label: `${names.of(code) ?? code} (${code})`,
            value: code,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, language));
}

export function getDefaultCurrencySchema(): Yup.StringSchema<CurrencyCode> {
    return Yup.string()
        .required(t("FieldBuilder_Error_Money_DefaultCurrency_Required"))
        .matches(CURRENCY_CODE_PATTERN);
}

export function getControlFieldTypeConditionalValues(
    fieldType: ControlFieldType
) {
    if (
        fieldType.type === "DataSheet" ||
        fieldType.type === "DataGrid" ||
        fieldType.type === "MoneyInput"
    ) {
        return [];
    }
    return getCommonFieldTypeConditionalValues(fieldType);
}

export function getControlFieldTypeDrawerBehavior(
    fieldType: ControlFieldType
): DrawerBehavior {
    if (
        fieldType.type === "DataSheet" ||
        fieldType.type === "DataGrid" ||
        fieldType.type === "MoneyInput"
    ) {
        return DEFAULT_DRAWER_BEHAVIOR;
    }
    return getCommonFieldTypeDrawerBehavior(fieldType);
}

export function getFieldSchema(
    dataSheetAssignmentsRef: MutableRefObject<DataSheetAssignmentsListDto>
): Yup.Schema<ControlField[]> {
    return parallelArray()
        .of(
            Yup.object({
                fieldType: Yup.lazy(
                    (
                        fieldType: ControlFieldType
                    ): Yup.ISchema<ControlFieldType> => {
                        switch (fieldType.type) {
                            case "DataSheet": {
                                return Yup.object({
                                    type: Yup.string()
                                        .oneOf(["DataSheet"])
                                        .defined(),
                                    dataSheetId: Yup.string()
                                        .matches(uuidRegEx, {
                                            name: "uuid",
                                            message: t(
                                                "Form_Error_Types_Regex",
                                                {
                                                    field: "dataSheetId",
                                                    type: "UUID",
                                                }
                                            ),
                                            excludeEmptyString: false,
                                        })
                                        .defined(),
                                    grouping: Yup.array().defined(),
                                })
                                    .defined()
                                    .test({
                                        name: "datasheet",
                                        test: (value, { createError }) => {
                                            const { dataSheetId, grouping } =
                                                value;
                                            const selectedDataSheetAssignments =
                                                dataSheetAssignmentsRef.current.content.find(
                                                    (dsa) =>
                                                        dsa.resource
                                                            .dataSheetId ===
                                                        dataSheetId
                                                );
                                            const selectedDataSheet =
                                                dataSheetAssignmentsRef.current.linkedResources.dataSheets.find(
                                                    (ds) =>
                                                        ds.id === dataSheetId
                                                );

                                            if (
                                                !selectedDataSheetAssignments ||
                                                !selectedDataSheet
                                            ) {
                                                return createError({
                                                    path: "dataSheetId",
                                                    message: t(
                                                        "FieldBuilder_Error_DataSheet"
                                                    ),
                                                });
                                            }

                                            // Note: it is not an error if the grouping no longer exists
                                            // XXX Should we check the data types though?
                                            const groupingColumns =
                                                selectedDataSheet.columns.filter(
                                                    (col) => col.groupingColumn
                                                );
                                            if (
                                                isEmpty(grouping) ||
                                                grouping.length !==
                                                    groupingColumns.length
                                            ) {
                                                return createError({
                                                    path: "grouping",
                                                    message: t(
                                                        "FieldBuilder_Error_DataSheet_Grouping"
                                                    ),
                                                });
                                            }

                                            return true;
                                        },
                                    });
                            }
                            case "DataGrid":
                                return Yup.object({
                                    type: Yup.string()
                                        .oneOf(["DataGrid"])
                                        .defined(),
                                    columns: Yup.array()
                                        .defined()
                                        .of(
                                            Yup.object({
                                                id: Yup.string()
                                                    .matches(uuidRegEx, {
                                                        name: "uuid",
                                                        message: t(
                                                            "Form_Error_Types_Regex",
                                                            {
                                                                field: "id",
                                                                type: "UUID",
                                                            }
                                                        ),
                                                        excludeEmptyString: false,
                                                    })
                                                    .defined(),
                                                title: Yup.string().required(
                                                    t(
                                                        "FieldBuilder_Error_DataGrid_Columns"
                                                    )
                                                ),
                                                type: Yup.string()
                                                    .oneOf(["Text", "Numeric"])
                                                    .defined(),
                                            })
                                        )
                                        .test(
                                            "unique-columns",
                                            (value, { createError }) => {
                                                const titleMap: Record<
                                                    string,
                                                    number[]
                                                > = {};
                                                value.forEach((val, index) => {
                                                    const trimmedTitle =
                                                        val.title
                                                            .trim()
                                                            .toLowerCase();
                                                    if (
                                                        titleMap[trimmedTitle]
                                                    ) {
                                                        titleMap[
                                                            trimmedTitle
                                                        ].push(index);
                                                    } else {
                                                        titleMap[trimmedTitle] =
                                                            [index];
                                                    }
                                                });
                                                const invalidIndices =
                                                    Object.values(titleMap)
                                                        .filter(
                                                            (indices) =>
                                                                indices.length >
                                                                1
                                                        )
                                                        .flatMap(
                                                            (indices) => indices
                                                        );
                                                if (invalidIndices.length > 0) {
                                                    return toSplitAtomValidationError(
                                                        createError({
                                                            message: t(
                                                                "FieldBuilder_Error_DataGrid_ColumnAlreadyExists"
                                                            ),
                                                        }),
                                                        invalidIndices
                                                    );
                                                }
                                                return true;
                                            }
                                        )
                                        .min(
                                            1,
                                            t(
                                                "FieldBuilder_Error_DataGrid_Columns"
                                            )
                                        )
                                        .max(
                                            DATAGRID_MAX_NUMBER_OF_COLUMNS,
                                            t(
                                                "FieldBuilder_Error_DataGrid_Columns"
                                            )
                                        ),
                                    initialNumberOfRows: Yup.number()
                                        .integer()
                                        .defined()
                                        .min(
                                            1,
                                            t(
                                                "FieldBuilder_Error_DataGrid_Rows"
                                            )
                                        )
                                        .max(
                                            DATAGRID_MAX_NUMBER_OF_ROWS,
                                            t(
                                                "FieldBuilder_Error_DataGrid_Rows"
                                            )
                                        ),
                                }).defined();
                            case "MoneyInput":
                                return Yup.object({
                                    type: Yup.string()
                                        .oneOf(["MoneyInput"])
                                        .defined(),
                                    defaultCurrency: getDefaultCurrencySchema(),
                                }).defined();
                            default:
                                return getCommonFieldTypeSchema(fieldType);
                        }
                    }
                ),
                ...getCommonFieldSchemaProperties(),
            }).defined()
        )
        .defined()
        .test({
            name: "condition",
            test: (values, { createError }) => {
                const PATH = "condition";
                const errorIndicesToMessages: Record<number, string> = {};
                values.forEach((value, index) => {
                    const condition = value.condition;
                    if (!condition) {
                        return;
                    }
                    const dependency = findDependency(
                        condition.fieldId,
                        values,
                        value.id
                    );
                    switch (dependency.type) {
                        case "FOUND": {
                            break;
                        }
                        case "FOUND_AFTER": {
                            errorIndicesToMessages[index] = t(
                                "FieldBuilder_Error_Condition_Dependency_FoundAfter"
                            );
                            return;
                        }
                        case "NOT_FOUND": {
                            errorIndicesToMessages[index] = t(
                                "FieldBuilder_Error_Condition_Dependency_NotFound"
                            );
                            return;
                        }
                        default: {
                            assertNever(dependency);
                        }
                    }

                    if (
                        !(
                            getControlFieldTypeConditionalValues(
                                dependency.value.fieldType
                            ).findIndex((v) => v === condition.value) >= 0
                        )
                    ) {
                        errorIndicesToMessages[index] = t(
                            "FieldBuilder_Error_Condition_Dependency_ValueNotFound"
                        );
                    }
                });

                if (Object.keys(errorIndicesToMessages).length > 0) {
                    return toSplitAtomValidationError(
                        createError({
                            path: PATH,
                        }),
                        errorIndicesToMessages
                    );
                }

                return true;
            },
        });
}

/**
 * `fieldType` is given when an existing task is copied. A copied Money task keeps its own default
 * currency, and a new one starts on `defaultCurrency`.
 */
export function createNewControlField(
    fieldTypeName: ControlFieldTypeName,
    fieldType: ControlFieldType | undefined,
    defaultCurrency: CurrencyCode | undefined
): ControlField {
    let newFieldType: ControlFieldType;
    switch (fieldTypeName) {
        case "DataSheet":
            const dataSheetFieldType: DataSheetFieldType = {
                type: "DataSheet",
                dataSheetId: "",
                grouping: [],
            };
            newFieldType = dataSheetFieldType;
            break;

        case "DataGrid":
            const dataGridFieldType = {
                type: "DataGrid",
                initialNumberOfRows: 2,
                columns: [
                    { id: generate(), title: "Col1", type: "Text" },
                    { id: generate(), title: "Col2", type: "Text" },
                    { id: generate(), title: "Col3", type: "Text" },
                ],
            } satisfies Extract<ControlFieldType, { type: "DataGrid" }>;
            newFieldType = dataGridFieldType;
            break;
        case "MoneyInput":
            if (fieldType?.type === "MoneyInput") {
                newFieldType = fieldType;
            } else if (defaultCurrency !== undefined) {
                newFieldType = { type: "MoneyInput", defaultCurrency };
            } else {
                throw new Error(
                    "Expected a default currency for a new money field"
                );
            }
            break;
        case "Heading":
        case "TextArea":
        case "Dropdown":
        case "TextInput":
        case "FileUpload":
        case "NumericInput":
        case "CheckboxGroup":
        case "RadioButtonGroup":
            if (fieldType?.type === "DataSheet") {
                throw new Error("Expected non-datasheet fieldType.type");
            }
            if (fieldType?.type === "DataGrid") {
                throw new Error("Expected non-datagrid fieldType.type");
            }
            if (fieldType?.type === "MoneyInput") {
                throw new Error("Expected non-money fieldType.type");
            }

            newFieldType = getCommonFieldTypeDefaultValue(
                fieldTypeName,
                fieldType
            );
            break;
        default:
            return assertNever(fieldTypeName);
    }

    const newField: ControlField = {
        id: generate(),
        title: getFieldTypeInfo()(fieldTypeName).label,
        instructions: "",
        fieldType: newFieldType,
        blobIds: [],
        mandatory: false,
        condition: null,
    };
    return newField;
}
